import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useToast } from "@/hooks/errors/use-toast";
import { AlertCircle, CreditCard, Zap, Activity, PieChart } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useSubscription } from '@/hooks/subscription/useSubscription';
import { SubscriptionStatus } from '@/types/subscription-types';
import Container from '@/components/ui/Container';
import DashboardHeader from '@/components/dashboard/DashboardHeader';
import CurrentPlanCard from './CurrentPlanCard';
import CreditTransactionsCard from './CreditTransactionsCard';
import usePlanData from '@/hooks/token/use-plan-data';
import { useDynamicFeatureConfig } from '@/config/dynamic-credit-features-config';
import { supabase } from '@/integrations/supabase/client';
import { GRID_BREAKPOINTS } from '@/config/grid-breakpoints';
import { useAIUsageCacheInvalidation } from '@/hooks/ai-usage/useAIUsageCacheInvalidation';

// Import Usage components
import { 
  UsageOverviewCards, 
  UsageAnalyticsSection, 
  UsageDetailsTable 
} from '@/components/usage';
import { motion } from 'framer-motion';
import { formatOperationType, getOperationTypeIcon } from '@/utils/operation-type-formatter';
import { useUsageAnalytics } from '@/hooks/usage/useUsageData';
import AIUsageCard from '@/components/subscription/AIUsageCard';
import BillingSkeleton from '@/components/subscription/BillingSkeleton';
import PlanCarouselContainer, { PlanActionButton } from '@/components/subscription/PlanCarouselContainer';

// Feature Breakdown Card Component
const FeatureBreakdownCard: React.FC = () => {
  const { data: analyticsData, isLoading } = useUsageAnalytics('30d');

  const featureData = React.useMemo(() => {
    if (!analyticsData) return [];
    
    return analyticsData
      .filter(item => 
        item.feature_name && 
        item.feature_percentage > 0 &&
        // Filter out internal research operations that users don't need to see
        !item.feature_name.includes('Research Generation') &&
        !item.feature_name.includes('Research Header Generation')
      )
      .map(item => {
        // Map feature names to operation types for proper icon/color display
        let operationType = '';
        if (item.feature_name === 'Recommended Tasks') {
          operationType = 'text_generation';
        } else if (item.feature_name === 'Recommended Research') {
          operationType = 'research';
        } else if (item.feature_name === 'Chat Conversation') {
          operationType = 'agent_chat';
        } else if (item.feature_name === 'Page Generation') {
          operationType = 'text_generation';
        } else if (item.feature_name === 'Image Generation') {
          operationType = 'image_generation';
        } else if (item.feature_name.toLowerCase().includes('research')) {
          operationType = 'research';
        } else {
          operationType = item.feature_name.toLowerCase().replace(/\s+/g, '_');
        }

        return {
          name: operationType,
          percentage: Math.round(item.feature_percentage || 0),
          color: operationType === 'agent_chat' ? 'amber' :
                 operationType === 'text_generation' ? 'blue' :
                 operationType === 'image_generation' ? 'red' :
                 operationType === 'research' ? 'emerald' :
                 operationType === 'threaded_generation' ? 'purple' : 'gray',
          originalName: item.feature_name // Keep original for display
        };
      })
      .sort((a, b) => b.percentage - a.percentage) // Sort by usage (highest first)
      .slice(0, 6); // Only show top 6 features
  }, [analyticsData]);

  if (isLoading) {
    return (
      <div className="relative rounded-2xl bg-gradient-to-br from-white/80 via-white/90 to-white/80 
                     dark:from-gray-900/80 dark:via-gray-900/90 dark:to-gray-900/80 
                     backdrop-blur-xl border border-white/20 dark:border-gray-700/30 
                     shadow-xl p-6 flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-purple-500 border-t-transparent"></div>
        <span className="ml-2 text-gray-600 dark:text-gray-400">Loading...</span>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: 0.1 }}
      className="relative rounded-2xl bg-gradient-to-br from-white/80 via-white/90 to-white/80 
                 dark:from-gray-900/80 dark:via-gray-900/90 dark:to-gray-900/80 
                 backdrop-blur-xl border border-white/20 dark:border-gray-700/30 
                 shadow-xl hover:shadow-2xl transition-all duration-300"
    >
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-amber-500/5 
                      dark:from-purple-500/10 dark:to-amber-500/10 rounded-2xl" />
      
      <div className="relative p-6">
        {/* Header */}
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-amber-600 
                         flex items-center justify-center shadow-lg">
            <PieChart className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              Feature Breakdown
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Usage by feature type
            </p>
          </div>
        </div>

        {/* Feature Breakdown */}
        <div className="space-y-4">
          {featureData.map((feature, index) => (
            <motion.div
              key={`${feature.name}-${index}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className="space-y-2"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-base">{getOperationTypeIcon(feature.name)}</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {feature.originalName || formatOperationType(feature.name)}
                  </span>
                </div>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {Math.round(feature.percentage)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${feature.percentage}%` }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`h-2 rounded-full bg-${feature.color}-500`}
                />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

const SubscriptionPage: React.FC = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const paymentStatus = query.get('status');
  const directPortal = query.get('portal'); // Check for direct portal access
  
  const { toast } = useToast();
  const { status, isLoading: subscriptionLoading, error: subscriptionError, refreshStatus, createCheckoutSession, openCustomerPortal, handleUpgradeAttempt } = useSubscription();
  const { subscriptionPlans, isLoading: plansLoading } = usePlanData();
  const { invalidateAIUsageDataHard } = useAIUsageCacheInvalidation();
  
  // Usage page state
  const [selectedTimeRange, setSelectedTimeRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d');
  
  // Direct token data state (replacing complex useTokenBalance)
  const [tokenData, setTokenData] = useState({
    isLoading: true,
    error: null as string | null,
    balance: null as any,
    usageStats: null as any,
    currentPlan: null as any
  });
  
  // Extract for compatibility with existing code
  const tokenLoading = tokenData.isLoading;
  const tokenError = tokenData.error;
  
  // Direct fetch function (replacing complex useTokenBalance)
  const fetchTokenBalance = useCallback(async (forceRefresh = false) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return;

      const { data, error } = await supabase
        .from('user_balance_with_usage')
        .select('*')
        .eq('user_id', userData.user.id)
        .single();

      if (error) throw error;

      setTokenData({
        isLoading: false,
        error: null,
        balance: {
          computed_balance: data?.computed_balance || 0,
          computed_monthly_usage: data?.computed_monthly_usage || 0
        },
        usageStats: {
          monthly_usage: data?.computed_monthly_usage || 0,
          usage_percentage: data?.usage_percentage || 0,
          remaining_balance: data?.remaining_balance || 0
        },
        currentPlan: {
          plan_name: data?.plan_name || 'Freemium'
        }
      });
    } catch (error) {
      setTokenData({
        isLoading: false,
        error: 'Failed to load token data',
        balance: null,
        usageStats: null,
        currentPlan: null
      });
    }
  }, []);
  const [currentPlanId, setCurrentPlanId] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState<boolean>(false);
  

  
  // NEW: Track selected plan for value proposition
  const [selectedPlan, setSelectedPlan] = useState<{name: string, price: number, tokens: number, id: string, priceId: string} | null>(null);
  
  // NEW: Shared upgrade loading state
  const [isUpgrading, setIsUpgrading] = useState<boolean>(false);
  
  // Removed handlePlanCreditsUpdate - now using selectedPlan state directly for calculations

  // NEW: Callback to update selected plan info with computed upgrade text
  const handleSelectedPlanUpdate = (planName: string, planPrice: number, planTokens: number, planId?: string, priceId?: string) => {
    if (planName === '' && planPrice === 0) {
      // Reset case
      setSelectedPlan(null);
    } else {
      setSelectedPlan({ 
        name: planName, 
        price: planPrice, 
        tokens: planTokens, 
        id: planId || '', 
        priceId: priceId || '' 
      });
    }
  };

  // NEW: Compute upgrade text for green button (same logic as orange button)
  const getUpgradeText = () => {
    if (!selectedPlan || !currentPlan) return '';
    
    const multiplier = Math.round(selectedPlan.tokens / currentPlan.aiTokens);
    const priceDifference = selectedPlan.price - currentPlan.price;
    return `${multiplier}x the productivity for only $${priceDifference} extra`;
  };
  
  // Get current plan data for calculations
  const currentPlan = subscriptionPlans.find(p => p.id === currentPlanId) || subscriptionPlans[0];
  
  // Get dynamic feature configuration based on live pricing data
  const { featureConfigs, isLoading: featuresLoading, calculateTotalUses } = useDynamicFeatureConfig();
  
  // Get feature breakdown data based on selected plan or current plan allocation
  const features = featureConfigs;
  const planTokens = selectedPlan?.tokens || currentPlan?.aiTokens || 0;
  const featureBreakdown = calculateTotalUses(planTokens);
  
  // Determine if there's a critical error that prevents showing content
  const hasCriticalError = tokenError && !tokenData.balance;
  
  // Handle successful or canceled payment/plan updates (just clear URL)
  useEffect(() => {
    if (paymentStatus === 'success' || paymentStatus === 'canceled' || paymentStatus === 'updated') {
      // Remove the query parameter from URL
      window.history.replaceState({}, document.title, window.location.pathname);
      
      if (paymentStatus === 'success') {
        toast({
          title: "Payment Status",
          description: "Your payment status has been updated.",
          variant: "success",
        });
        
        // Refresh token balance after successful payment
        fetchTokenBalance(true);
        refreshStatus();
        // Hard invalidate AI usage cache to see updated credits immediately
        invalidateAIUsageDataHard();
      } else if (paymentStatus === 'updated') {
        toast({
          title: "Plan Updated",
          description: "Your subscription has been successfully updated.",
          variant: "success",
        });
        
        // Refresh data after plan update
        fetchTokenBalance(true);
        refreshStatus();
        // Hard invalidate AI usage cache to see updated credits immediately
        invalidateAIUsageDataHard();
      } else {
        toast({
          title: "Payment Canceled",
          description: "The payment process was canceled.",
          variant: "warning",
        });
      }
    }
  }, [paymentStatus, invalidateAIUsageDataHard]); // Add invalidateAIUsageDataHard to dependencies

  // Determine the current plan from subscription status (user's actual plan)
  useEffect(() => {
    if (status?.subscription?.plan_name) {
      // Map plan name to plan ID for the dropdown
      const planName = status.subscription.plan_name.toLowerCase();
      // Handle plan name variations (e.g., "AI Starter" -> "ai starter")
      const normalizedPlan = planName.replace(/\s+/g, ' ').trim();
      setCurrentPlanId(normalizedPlan);
    } else {
      // Fallback to freemium if no subscription
      setCurrentPlanId('freemium');
    }
  }, [status?.subscription?.plan_name]); // Re-run when current plan changes
  
  // Fetch subscription status on component mount
  useEffect(() => {
    refreshStatus();
  }, []); // Only run once on mount
  
  // Fetch token data on component mount
  useEffect(() => {
    fetchTokenBalance();
  }, [fetchTokenBalance]);
  
  // Handle retry when experiencing token data error
  const handleRetryTokenFetch = async () => {
    if (isRetrying) return;
    
    setIsRetrying(true);
    try {
      await fetchTokenBalance(true);
      toast({
        title: "Refresh",
        description: "Successfully refreshed token information.",
        variant: "success",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to refresh token information. Please try again later.",
        variant: "error",
      });
    } finally {
      setIsRetrying(false);
    }
  };

  const handleSelectPlan = async (planId: string, priceId: string) => {
    const selectedPlanData = subscriptionPlans.find(p => p.id === planId);
    const currentPlanData = subscriptionPlans.find(p => p.id === currentPlanId);
    
    // Handle downgrade to Freemium (cancellation)
    if (planId === 'freemium' || (selectedPlanData?.price === 0 && (currentPlanData?.price || 0) > 0)) {
      setIsUpgrading(true);
      try {
        toast({
          title: "Opening subscription management",
          description: "Redirecting to cancel your subscription...",
          variant: "default",
        });
        await openCustomerPortal();
      } catch (error) {
        console.error('Portal access error:', error);
        toast({
          title: "Error",
          description: "Failed to open subscription management. Please try again.",
          variant: "error",
        });
      } finally {
        setIsUpgrading(false);
      }
      return;
    }
    
    // Handle already on selected plan
    if (planId === currentPlanId) {
      toast({
        title: "Current Plan",
        description: "You're already on this plan.",
        variant: "info",
      });
      return;
    }
    
    // Validate plan has price ID for paid plans
    if (!priceId && selectedPlanData?.price && selectedPlanData.price > 0) {
      toast({
        title: "Plan Unavailable",
        description: "This plan is not available for purchase at this time.",
        variant: "error",
      });
      return;
    }

    setIsUpgrading(true);
    try {
      // Use the new handleUpgradeAttempt method which includes fallback logic
      await handleUpgradeAttempt(priceId);
    } catch (error) {
      console.error('Plan selection error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to process plan change';
      toast({
        title: "Plan Change Error",
        description: errorMessage,
        variant: "error",
      });
    } finally {
      setIsUpgrading(false);
    }
  };
  
  // Removed handleUpgradeFromCalculator - no longer needed since orange button is removed

  const handleManageSubscription = async () => {
    await openCustomerPortal();
  };



  // Direct portal access - bypass all loading and go straight to Stripe
  useEffect(() => {
    if (directPortal === 'true') {
      const redirectToPortal = async () => {
        try {
          const { data, error } = await supabase.functions.invoke('customer-portal', {
            body: { 
              returnUrl: `${window.location.origin}/billing`
            }
          });

          if (error) {
            toast({
              title: "Portal Access Error",
              description: error.message || 'Failed to access customer portal',
              variant: "error",
            });
            // Remove portal param and show regular billing page
            window.history.replaceState({}, '', '/billing');
            return;
          }
          
          if (data?.url) {
            window.location.href = data.url;
          } else {
            toast({
              title: "Portal Access Error", 
              description: 'No portal URL received from Stripe',
              variant: "error",
            });
            // Remove portal param and show regular billing page
            window.history.replaceState({}, '', '/billing');
          }
        } catch (err) {
          console.error('Direct portal access error:', err);
          toast({
            title: "Portal Access Error",
            description: 'Failed to access customer portal',
            variant: "error",
          });
          // Remove portal param and show regular billing page
          window.history.replaceState({}, '', '/billing');
        }
      };

      redirectToPortal();
    }
  }, [directPortal, toast]);

  // Show loading spinner for direct portal access
  if (directPortal === 'true') {
    return (
      <Container>
        <DashboardHeader 
          title="Choose Your Plan"
          description="Select the perfect plan for your needs. Plans can be changed anytime."
          icon={<CreditCard size={24} />}
          hideOnMobile={false}
        />
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Taking you to Stripe Customer Portal...
            </p>
          </div>
        </div>
      </Container>
    );
  }
  
  const isLoading = subscriptionLoading || plansLoading || tokenLoading || isRetrying || featuresLoading;

  if (isLoading) {
    return <BillingSkeleton />;
  }
  
  // Error states with retry options
  if (hasCriticalError || subscriptionError) {
    return (
      <Container>
        <DashboardHeader 
          title="Choose Your Plan"
          description="Select the perfect plan for your needs. Plans can be changed anytime."
          icon={<CreditCard size={24} />}
          hideOnMobile={false}
        />
        
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex flex-col space-y-2">
            <span>
              {tokenError ? 'Could not load your token data. ' : ''}
              {subscriptionError || 'An error occurred loading subscription data.'}
            </span>
            <button 
              onClick={handleRetryTokenFetch}
              className="text-sm text-white bg-red-700 hover:bg-red-800 px-3 py-1 rounded self-start"
            >
              Retry
            </button>
          </AlertDescription>
        </Alert>
        
        {/* Show limited UI even with errors */}
        <div className={`grid ${GRID_BREAKPOINTS.billingMain} gap-6 opacity-75`}>
          <div className={`grid ${GRID_BREAKPOINTS.billingErrorCards} gap-6`}>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md border border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3 mb-4">
                <CreditCard className="h-8 w-8 text-purple-500" />
                <h2 className="text-xl font-semibold">Billing</h2>
              </div>
              <p className="text-gray-600 dark:text-gray-300">
                Your subscription data could not be loaded. Please try again later.
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md border border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3 mb-4">
                <Zap className="h-8 w-8 text-amber-500" />
                <h2 className="text-xl font-semibold">Token Balance</h2>
              </div>
              <p className="text-gray-600 dark:text-gray-300">
                Your token balance could not be loaded. Please try again later.
              </p>
            </div>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <DashboardHeader 
        title="Choose Your Plan"
        description="Select the perfect plan for your needs. All plans include AI features and can be changed anytime."
        icon={<CreditCard size={24} />}
        hideOnMobile={false}
      />
      
      {(tokenError || subscriptionError) && (
        <Alert variant="warning" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {tokenError || subscriptionError}
            <button
              onClick={handleRetryTokenFetch}
              className="ml-2 text-sm underline hover:no-underline"
            >
              Retry
            </button>
          </AlertDescription>
        </Alert>
      )}
      
      {/* NEW: Plan Carousel Section with Action Button and AI Usage Layout */}
      <div className="mb-8">
        {/* Carousel Cards */}
        <PlanCarouselContainer
          plans={subscriptionPlans}
          currentPlanId={currentPlanId}
          selectedPlan={selectedPlan}
          onSelectPlan={handleSelectPlan}
          onSelectedPlanUpdate={handleSelectedPlanUpdate}
          isUpgrading={isUpgrading}
          renderActionExternal={true}
        />
        
        {/* Action Upgrade Card - Centered */}
        <div className="flex justify-center">
          <div className="w-full max-w-2xl">
            <PlanActionButton
              selectedPlanData={subscriptionPlans.find(p => p.id === selectedPlan?.id)}
              currentPlanId={currentPlanId}
              isUpgrading={isUpgrading}
              onSelectPlan={handleSelectPlan}
              plans={subscriptionPlans}
            />
          </div>
        </div>
      </div>
      
      <div className={`grid ${GRID_BREAKPOINTS.billingMain} gap-6`}>
        {/* BILLING SECTION - Side by Side Layout */}
        <div className={`grid ${GRID_BREAKPOINTS.billingSectionSplit} gap-6`}>
          {/* Left: Credit Deposits */}
          <CreditTransactionsCard 
            currentPlan={currentPlan}
            onManageSubscription={handleManageSubscription}
          />
          
          {/* Right: Feature Breakdown */}
          <FeatureBreakdownCard />
        </div>
        
        {/* Usage Details Table - Full Width */}
        <div className="hidden sm:block">
          <UsageDetailsTable 
            timeRange={selectedTimeRange}
          />
        </div>
      </div>
    </Container>
  );
};

export default SubscriptionPage;
